import {
  findImportTargetField,
  type ImportColumnDecision,
  type ImportItemType,
  type ImportMappingIssue,
  importTargetFields,
} from "@lafayette/import-mapping";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";

import { checkLabel, checkRow, fieldError } from "../../catalog/components/styles";
import { itemTypeOptions } from "../../catalog/labels";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { DataTable } from "../../ui/data-table";
import { SelectField } from "../../ui/fields";
import { Label } from "../../ui/label";
import { Select } from "../../ui/select";
import { StatusBadge } from "../../ui/status-badge";
import { columnLabel, columnLetter, type ImportColumnView } from "../columns";
import { StepCard } from "../components/StepCard";
import { stepActions } from "../components/styles";
import {
  acceptSuggestion,
  type ColumnDecisions,
  canRemember,
  describeCounts,
  ignoreColumn,
  isItemTypeMapped,
  mapColumn,
  mappingProblems,
  problemsByColumn,
  setRemember,
  unmapColumn,
} from "../decisions";
import { columnNoteLabels, mappingIssueMessages, targetGroupLabels } from "../labels";

const selectIdOf = (columnIndex: number) => `import-field-${columnIndex}`;

export type FocusRequest = Readonly<{ index: number; nonce: number }>;

type MapStepProps = Readonly<{
  columns: readonly ImportColumnView[];
  decisions: ColumnDecisions;
  defaultItemType: ImportItemType | null;
  /** The job is not in MAPPING any more: show the choices, change nothing. */
  readOnly: boolean;
  /** Staff tried to submit: an undecided column now shows its error. */
  attempted: boolean;
  /** Issues the server named in a 422 (codes and column indexes only). */
  serverIssues: readonly ImportMappingIssue[];
  focusRequest: FocusRequest | null;
  rerunBusy: boolean;
  onDecisions: (next: ColumnDecisions) => void;
  onDefaultItemType: (next: ImportItemType | null) => void;
  onRerun: () => void;
  onContinue: () => void;
}>;

const groups = (["common", "event", "dining", "villa", "experience", "service"] as const).map(
  (group) => ({
    group,
    fields: importTargetFields.filter((field) => field.group === group),
  }),
);

const targetLabel = (key: string | null) =>
  key === null ? "" : (findImportTargetField(key)?.label ?? key);

function StatusCell({
  decision,
  column,
  readOnly,
  onAccept,
}: Readonly<{
  decision: ImportColumnDecision;
  column: ImportColumnView;
  readOnly: boolean;
  onAccept: () => void;
}>) {
  if (decision.decision === "MAPPED") {
    return decision.origin === "SUGGESTION" ? (
      <StatusBadge tone="success">Suggested</StatusBadge>
    ) : (
      <StatusBadge tone="success">Mapped</StatusBadge>
    );
  }
  if (decision.decision === "IGNORED") {
    return <StatusBadge tone="neutral">Ignored</StatusBadge>;
  }
  if (decision.suggestedTargetField !== null) {
    const label = targetLabel(decision.suggestedTargetField);
    return (
      <span className="grid justify-items-start gap-1">
        <StatusBadge tone="warning" glyph="?" className="whitespace-normal">
          <span>
            Did you mean <em>{label}</em>?
          </span>
        </StatusBadge>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={readOnly}
          aria-label={`Accept ${label} for Column ${columnLetter(column.index)}`}
          onClick={onAccept}
        >
          Accept
        </Button>
      </span>
    );
  }
  return (
    <StatusBadge tone="warning" className="whitespace-normal">
      Not mapped
    </StatusBadge>
  );
}

/** One source column as the table shows it, with everything its cells need. The column list below never changes, so a cell keeps its focus. */
type MapRow = Readonly<{
  column: ImportColumnView;
  decision: ImportColumnDecision;
  label: string;
  selectId: string;
  shownIssue: ImportMappingIssue | undefined;
  readOnly: boolean;
  onSelect: (value: string) => void;
  onAccept: () => void;
  onIgnore: (ignored: boolean) => void;
  onRemember: (remember: boolean) => void;
}>;

const mapColumnHelper = createColumnHelper<MapRow>();

const mapColumns = [
  mapColumnHelper.display({
    id: "source",
    header: "Source column",
    meta: { rowHeader: true, className: "min-w-40 font-semibold [overflow-wrap:anywhere]" },
    cell: (info) => {
      const { label, column } = info.row.original;
      return (
        <>
          {label}
          {column.notes.length > 0 ? (
            <ul className="m-0 mt-1 flex list-none flex-wrap gap-1 p-0 font-normal">
              {column.notes.map((note) => (
                <li key={note}>
                  <Badge tone="outline">{columnNoteLabels[note]}</Badge>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      );
    },
  }),
  mapColumnHelper.display({
    id: "samples",
    header: "Sample values",
    cell: (info) => {
      const { column } = info.row.original;
      return column.samples.length === 0 ? (
        <span className="text-sm text-muted-foreground">No values</span>
      ) : (
        <ul className="m-0 grid list-none gap-1 p-0">
          {column.samples.map((sample, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: samples may repeat.
            <li key={index} className="whitespace-pre-wrap [overflow-wrap:anywhere]">
              {sample}
            </li>
          ))}
        </ul>
      );
    },
  }),
  mapColumnHelper.display({
    id: "field",
    header: "Lafayette field",
    cell: (info) => {
      const { decision, label, selectId, shownIssue, readOnly, onSelect } = info.row.original;
      const errorId = `${selectId}-error`;
      return (
        <div className="grid gap-1">
          <Label htmlFor={selectId} className="sr-only">
            Lafayette field for {label}
          </Label>
          <Select
            id={selectId}
            className="min-w-38"
            value={decision.decision === "MAPPED" ? (decision.targetField ?? "") : ""}
            disabled={readOnly || decision.decision === "IGNORED"}
            aria-invalid={shownIssue === undefined ? undefined : true}
            aria-describedby={shownIssue === undefined ? undefined : errorId}
            onChange={(event) => onSelect(event.currentTarget.value)}
          >
            <option value="">{decision.decision === "IGNORED" ? "Ignored" : "Not mapped"}</option>
            {groups.map(({ group, fields }) => (
              <optgroup key={group} label={targetGroupLabels[group]}>
                {fields.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          {shownIssue === undefined ? null : (
            <p id={errorId} className={fieldError}>
              <span aria-hidden="true">⚠ </span>
              {shownIssue.code === "COLUMN_UNDECIDED"
                ? "Choose a field for this column, or ignore it."
                : mappingIssueMessages[shownIssue.code]}
            </p>
          )}
        </div>
      );
    },
  }),
  mapColumnHelper.display({
    id: "status",
    header: "Status",
    cell: (info) => {
      const { decision, column, readOnly, onAccept } = info.row.original;
      return (
        <StatusCell decision={decision} column={column} readOnly={readOnly} onAccept={onAccept} />
      );
    },
  }),
  mapColumnHelper.display({
    id: "options",
    header: "Options",
    cell: (info) => {
      const { decision, column, label, readOnly, onIgnore, onRemember } = info.row.original;
      return (
        <>
          <div className={checkRow}>
            <Checkbox
              id={`import-ignore-${column.index}`}
              checked={decision.decision === "IGNORED"}
              disabled={readOnly}
              onChange={(event) => onIgnore(event.currentTarget.checked)}
            />
            <Label htmlFor={`import-ignore-${column.index}`} className={checkLabel}>
              Ignore<span className="sr-only"> {label}</span>
            </Label>
          </div>
          {canRemember(decision) ? (
            <div className={checkRow}>
              <Checkbox
                id={`import-remember-${column.index}`}
                checked={decision.remember}
                disabled={readOnly}
                onChange={(event) => onRemember(event.currentTarget.checked)}
              />
              <Label htmlFor={`import-remember-${column.index}`} className={checkLabel}>
                Remember for next imports
                <span className="sr-only"> ({label})</span>
              </Label>
            </div>
          ) : null}
        </>
      );
    },
  }),
];

/**
 * Step 3. One row per source column: its label (a letter and the header, so a duplicate or blank
 * header is still told apart), three sample values, the target field, a status and the options. A
 * HIGH suggestion arrives applied and marked "Suggested". A MEDIUM suggestion is only shown, with an
 * Accept button: it is never applied alone. Submit stays blocked while a column is not mapped.
 */
export function MapStep({
  columns,
  decisions,
  defaultItemType,
  readOnly,
  attempted,
  serverIssues,
  focusRequest,
  rerunBusy,
  onDecisions,
  onDefaultItemType,
  onRerun,
  onContinue,
}: MapStepProps) {
  const problems = problemsByColumn(mappingProblems(decisions, defaultItemType, "submit"));
  const serverByColumn = problemsByColumn(serverIssues);
  const itemTypeMapped = isItemTypeMapped(decisions);

  const focusNonce = focusRequest?.nonce;
  const focusIndex = focusRequest?.index;
  useEffect(() => {
    if (focusNonce === undefined || focusIndex === undefined) return;
    document.getElementById(selectIdOf(focusIndex))?.focus();
  }, [focusNonce, focusIndex]);

  const decide = (next: ColumnDecisions) => {
    onDecisions(next);
    // Mapping a column to Item type replaces the item type of the whole import.
    if (isItemTypeMapped(next) && defaultItemType !== null) onDefaultItemType(null);
  };

  const rows: MapRow[] = columns.flatMap((column) => {
    const decision = decisions.find((d) => d.columnIndex === column.index);
    if (decision === undefined) return [];
    const serverIssue = serverByColumn.get(column.index);
    const problem = problems.get(column.index);
    // A duplicate target always shows. An undecided column shows after a submit attempt.
    const shownIssue =
      serverIssue ??
      (problem?.code === "TARGET_DUPLICATE" || (attempted && problem?.code === "COLUMN_UNDECIDED")
        ? problem
        : undefined);
    return [
      {
        column,
        decision,
        label: columnLabel(column),
        selectId: selectIdOf(column.index),
        shownIssue,
        readOnly,
        onSelect: (value: string) =>
          decide(
            value === ""
              ? unmapColumn(decisions, column.index)
              : mapColumn(decisions, column.index, value),
          ),
        onAccept: () => decide(acceptSuggestion(decisions, column.index)),
        onIgnore: (ignored: boolean) =>
          decide(
            ignored ? ignoreColumn(decisions, column.index) : unmapColumn(decisions, column.index),
          ),
        onRemember: (remember: boolean) => decide(setRemember(decisions, column.index, remember)),
      },
    ];
  });
  const tableColumns = useMemo(() => mapColumns, []);

  return (
    <StepCard step="map" title="Map columns">
      <p>
        Choose the Lafayette field for each source column, or ignore the column. A suggestion that
        is not certain is shown but never applied: you accept it or choose another field.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="m-0 font-semibold" role="status" aria-live="polite" aria-atomic="true">
          {describeCounts(decisions)}
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={onRerun}
          disabled={readOnly || rerunBusy}
        >
          {rerunBusy ? "Working…" : "Re-run suggestions"}
        </Button>
      </div>

      <DataTable
        banded
        columns={tableColumns}
        data={rows}
        getRowId={(row) => String(row.column.index)}
        caption="Source columns and the Lafayette field each one fills. Each column is identified by its letter."
      />

      <div className="max-w-sm">
        <SelectField
          id="import-default-item-type"
          label="Item type for every row"
          value={itemTypeMapped ? "" : (defaultItemType ?? "")}
          placeholder="Not set"
          options={itemTypeOptions}
          disabled={readOnly || itemTypeMapped}
          hint={
            itemTypeMapped
              ? "A column is mapped to Item type, so each row takes its type from that column."
              : "Use this when no column holds the item type."
          }
          onChange={(value) => onDefaultItemType(value === "" ? null : (value as ImportItemType))}
        />
      </div>

      <div className={stepActions}>
        <Button type="button" onClick={onContinue}>
          Continue to the review
        </Button>
      </div>
    </StepCard>
  );
}
